import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Text, Button, Modal } from "react_reusable_components";

import { ThreeRow, TwoColumn, TwoRow } from "../../core/layouts";
import AssignSubProductToQuestionnaireLayout from "../../layouts/AssignSubProductToQuestionnaireLayout";
import AssignSubProductCategory from "../../components/AssignSubProductCategory/AssignSubProductCategory";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import QuestionnaireService from "../../services/questionnaire-service";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import variables from "../../themes/base/styles/variables";
import { showNotification } from "../../UtilityFunctions/notification";

const modalStyles = {
  assignedSubProductHeading: {
    fontSize: variables.fontSizeXlarge,
    lineHeight: variables.lineHeightXxlarge,
    fontWeight: variables.fontWeightBold,
    color: variables.textDefault,
  },
  assignedSubProductDescription: {
    fontSize: variables.fontSizeSmallest,
    lineHeight: variables.lineHeightMedium,
    fontWeight: variables.fontWeightNormal,
    color: variables.grey1,
  },
  CancelButton: {
    fontSize: variables.fontSizeSmall,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    flex: 1,
    border: `1px solid ${variables.grey4}`,
  },
  AssignButton: {
    fontSize: variables.fontSizeSmall,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    flex: 1,
    backgroundColor: variables.primaryBg,
  },
  modalMaskStyle: {
    backdropFilter: variables.highBlur,
    background: variables.blurBackground,
  },
  btnContainer: {
    display: variables.dFlex,
    gap: variables.gapMediumSmall,
    justifyContent: "flex-end",
  },
  centerImg: {
    display: variables.dFlex,
    justifyContent: variables.alignCenter,
    position: "relative",
  },
  cross: {
    position: "absolute",
    right: "0",
    top: "0",
    height: variables.iconHeightLarge,
    width: variables.iconWidthLarge,
    cursor: "pointer",
  },
  topSectionStyle: {
    gap: variables.gapXS,
  },
  middleSectionStyle: {
    marginTop: variables.marginMediumSmall,
    marginBottom: variables.marginMedium,
  },
  leftSectionStyle: {
    gap: variables.gapXS,
    width: variables.fullWidth,
  },
  bottomSectionStyle: {
    justifyContent: "flex-end",
  },
};

export default function AssignSubProductCategoryToQuestionnaireModal({
  open,
  onClose,
  questionnaireName,
  productCategory,
  productSubCategory,
  submitHandler,
  questionnaireId,
}) {
  const intl = useIntl();
  const loaderDispatch = useContext(GlobalLoaderContext)[1];

  const [productOptions, setProductOptions] = useState([]);
  const [subProductCategoryOptions, setSubProductCategoryOptions] = useState(
    []
  );
  const [lastProductSelected, setLastProductSelected] = useState("");
  const [
    subProductCategoryAlreadyAssignedModal,
    setSubProductCategoryAlreadyAssignedModal,
  ] = useState(false);
  const [selectedSubProductCategoryId, setSelectedSubProductCategoryId] =
    useState("");
  const [questionnaireModalData, setQuestionnaireModalData] = useState({});

  useEffect(() => {
    if (productCategory?.id && productSubCategory?.id) {
      setLastProductSelected(productCategory?.id);
      setSelectedSubProductCategoryId(productSubCategory?.id);
    } else {
      setLastProductSelected(productOptions?.[0]?.id);
    }
  }, [productOptions]);

  useEffect(() => {
    QuestionnaireService.getAllProductsCategory({ loaderDispatch, intl }).then(
      (result) => {
        let response = result?.result?.map((product) => ({
          ...product,
          id: product?.productCategoryId,
          name: product?.productCategoryName,
        }));
        setProductOptions(response);
      }
    );
  }, []);

  useEffect(() => {
    if (lastProductSelected) {
      let currentProductSelected = productOptions?.find(
        (product) => product?.id === lastProductSelected
      );
      setSubProductCategoryOptions(currentProductSelected?.subCategory);
    }
  }, [lastProductSelected, productOptions]);

  const productItemClickHandler = (productId) => () => {
    setLastProductSelected(productId);
  };

  const handleSubProductCheckHandler = (subProductId) => {
    setSelectedSubProductCategoryId(subProductId);
  };

  const assignSubProductCategoryToQuestionnaireHandler = () => {
    let productSubCategoryId = selectedSubProductCategoryId;

    if (!lastProductSelected) {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "error.pleaseSelect.product.Msg")
      );
      return;
    } else if (!productSubCategoryId) {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "error.pleaseSelect.Subproduct.Msg")
      );
      return;
    }

    QuestionnaireService.checkSubProductCategoryAlreadyAssignedToQuestionnaire({
      loaderDispatch,
      params: {
        condition: "productSubCategory",
        productSubCategoryId: productSubCategoryId,
      },
      intl,
    }).then((response) => {
      const result = response;
      if (result?.result?.id && result?.result?.id !== questionnaireId) {
        setSubProductCategoryAlreadyAssignedModal(true);
        setQuestionnaireModalData(result?.result);
      } else {
        submitHandler(productSubCategoryId);
        onClose();
      }
    });
  };

  return (
    <>
      <Modal
        maskClosable={false}
        maskStyle={modalStyles.modalMaskStyle}
        centered
        open={open}
        closable={false}
        footer={null}
      >
        <ThreeRow
          style={{ width: variables.fullWidth }}
          topSection={
            <TwoColumn
              leftSection={
                <TwoRow
                  style={modalStyles.topSectionStyle}
                  topSection={
                    <Text style={modalStyles.assignedSubProductHeading}>
                      {getLocalizedMessage(
                        intl,
                        "assign.subProduct.toQuestionnaire"
                      )}
                    </Text>
                  }
                  bottomSection={
                    <Text style={modalStyles.assignedSubProductDescription}>
                      {getLocalizedMessage(
                        intl,
                        "select.subProduct.toQuestionnaire",
                        { name: questionnaireName }
                      )}
                    </Text>
                  }
                />
              }
              leftSectionStyle={modalStyles.leftSectionStyle}
              rightSection={
                <div style={modalStyles.centerImg}>
                  <img
                    style={modalStyles.cross}
                    src={"/icons/close.svg"}
                    alt="close"
                    onClick={onClose}
                  />
                </div>
              }
            />
          }
          middleSectionStyle={modalStyles.middleSectionStyle}
          middleSection={
            <AssignSubProductToQuestionnaireLayout
              leftSection={
                <AssignSubProductCategory
                  options={productOptions}
                  placeholder={getLocalizedMessage(
                    intl,
                    "searchProducts.placeholder.searchProductCategory"
                  )}
                  showRightArrows
                  onItemSelect={productItemClickHandler}
                  lastProductSelected={lastProductSelected}
                  searchedKey="productCategoryName"
                />
              }
              rightSection={
                <AssignSubProductCategory
                  options={subProductCategoryOptions}
                  placeholder={getLocalizedMessage(
                    intl,
                    "searchSubProducts.placeholder.searchSubProductCategory"
                  )}
                  isRadioButtonsVisible
                  handleProductCheck={handleSubProductCheckHandler}
                  selectedSubProductCategoryId={selectedSubProductCategoryId}
                  searchedKey="name"
                />
              }
            />
          }
          bottomSectionStyle={modalStyles.bottomSectionStyle}
          bottomSection={
            <TwoColumn
              style={modalStyles.btnContainer}
              leftSection={
                <Button
                  style={modalStyles.CancelButton}
                  icon={null}
                  type="text"
                  onClick={onClose}
                >
                  {getLocalizedMessage(intl, "button.text.cancel")}
                </Button>
              }
              rightSection={
                <Button
                  style={modalStyles.AssignButton}
                  icon={null}
                  onClick={assignSubProductCategoryToQuestionnaireHandler}
                >
                  {getLocalizedMessage(intl, "button.text.assign")}
                </Button>
              }
            />
          }
        />
      </Modal>

      <ConfirmModalContainer
        open={subProductCategoryAlreadyAssignedModal}
        setOpen={setSubProductCategoryAlreadyAssignedModal}
        question={getLocalizedMessage(intl, "assinged.subCategory.modal.text", {
          subCategoryName: questionnaireModalData?.productSubCategoryName,
          questionnaireName: questionnaireModalData?.questionnaireName,
        })}
        iconSrc={"/icons/warningIcon.svg"}
        descriptionText={getLocalizedMessage(
          intl,
          "manageQuestionnaire.replace.modal.description"
        )}
        restStyle={{ backgroundColor: variables.primaryBg }}
        cancelText={getLocalizedMessage(intl, "button.text.no")}
        confirmText={getLocalizedMessage(intl, "button.text.yes")}
        onConfirm={() => {
          submitHandler(selectedSubProductCategoryId);
          setSubProductCategoryAlreadyAssignedModal(false);
          onClose();
        }}
      />
    </>
  );
}
